// ui/web/src/data/buzz.ts
import ky from "ky"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import type {
  BuzzSessionToken, BuzzRelay, CreateBuzzRelayInput,
  BuzzMemberList, BulkProvisionResult, BuzzAuditLogList,
  BuzzAuditVerifyResult,
} from "./buzz.types"

const api = ky.create({ prefixUrl: "/api/v1" })

// ─── USER PREFERENCES ───

/** Shape returned by GET /api/v1/me/preferences */
export interface UserPreferences {
  buzz_enabled: boolean
}

/** Fetch the current user's preferences, including buzz_enabled opt-in state. */
export function useUserPreferences() {
  return useQuery({
    queryKey: ["me", "preferences"],
    queryFn: () => api.get("me/preferences").json<UserPreferences>(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/** Update the current user's preferences. */
export function useUpdatePreferences() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (prefs: Partial<UserPreferences>) =>
      api.put("me/preferences", { json: prefs }).json<UserPreferences>(),
    onSuccess: (data) => {
      qc.setQueryData(["me", "preferences"], data)
      // Invalidate session-token cache so Buzz reconnects immediately after enabling.
      qc.invalidateQueries({ queryKey: ["buzz", "session-token"] })
    },
  })
}

// ─── SESSION TOKEN ───

/**
 * Fetch Buzz session token. Token TTL = 15 min.
 * Auto-refresh at 14 min interval before expiry.
 * Only runs when enabled=true (user has opted in to Buzz).
 */
export function useBuzzSessionToken(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["buzz", "session-token"],
    queryFn: () => api.post("buzz/session-token").json<BuzzSessionToken>(),
    enabled: opts?.enabled !== false,
    refetchInterval: 14 * 60 * 1000,    // 14 minutes
    staleTime: 13 * 60 * 1000,          // 13 minutes
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30_000),
  })
}

// ─── RELAY QUERIES ───

export function useBuzzRelays(opts?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: ["buzz", "relays"],
    queryFn: () => api.get("buzz/relays").json<BuzzRelay[]>(),
    refetchInterval: opts?.refetchInterval,
  })
}

// ─── MEMBER QUERIES ───

export function useBuzzMembers() {
  return useQuery({
    queryKey: ["buzz", "members"],
    queryFn: () => api.get("buzz/members").json<BuzzMemberList>(),
  })
}

// ─── AUDIT LOG QUERIES ───

export function useBuzzAuditLogs(opts: { limit?: number; relayId?: string }) {
  return useQuery({
    queryKey: ["buzz", "audit-logs", opts],
    queryFn: () =>
      api.get("buzz/audit-logs", {
        searchParams: {
          ...(opts.limit   && { limit:    String(opts.limit) }),
          ...(opts.relayId && { relay_id: opts.relayId }),
        },
      }).json<BuzzAuditLogList>(),
  })
}

// ─── RELAY MUTATIONS ───

export function useCreateBuzzRelay() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateBuzzRelayInput) =>
      api.post("buzz/relays", { json: data }).json<BuzzRelay>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["buzz", "relays"] }),
  })
}

export function useSetDefaultBuzzRelay() {
  const qc = useQueryClient()
  return useMutation({
    // buzz-adapter only ever registered this as POST
    // (v1.POST("/relays/:id/set-default", ...), cmd/server/main.go) — this
    // PATCH call 405'd on every "Set as Default" click. Fixed on the
    // frontend rather than adding a PATCH route to buzz-adapter: it's the
    // service's one and only action-style endpoint, POST is its existing
    // (if implicit) convention, and other callers may already exist for
    // the POST route (see SOL doc for the full reasoning).
    mutationFn: (relayId: string) =>
      api.post(`buzz/relays/${relayId}/set-default`).json(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["buzz", "relays"] }),
  })
}

export function useDeleteBuzzRelay() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (relayId: string) => api.delete(`buzz/relays/${relayId}`).json(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["buzz", "relays"] }),
  })
}

// ─── MEMBER MUTATIONS ───

export function useBulkProvisionBuzz() {
  const qc = useQueryClient()
  return useMutation({
    // BZ-04 fix: send UserIDs + RelayID in body (previously empty — backend provisioned 0 users).
    // The backend BulkProvision handler reads body.UserIDs and body.RelayID; OrgID is
    // overwritten from the X-Org-ID header (security fix already applied on backend).
    mutationFn: ({ userIds, relayId }: { userIds: string[]; relayId?: string }) =>
      api.post("buzz/members/bulk-provision", {
        json: { UserIDs: userIds, RelayID: relayId ?? "" },
      }).json<BulkProvisionResult>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["buzz", "members"] }),
  })
}


export function useRetryBuzzProvision() {
  const qc = useQueryClient()
  return useMutation({
    // POST, not PATCH — matches buzz-adapter's action-endpoint convention
    // (set-default above is the same story). See SOL doc.
    mutationFn: (userId: string) => api.post(`buzz/members/${userId}/retry`).json(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["buzz", "members"] }),
  })
}

export function useSuspendBuzzMember() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => api.post(`buzz/members/${userId}/suspend`).json(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["buzz", "members"] }),
  })
}

// ─── AUDIT MUTATIONS ───

export function useVerifyBuzzAuditChain() {
  return useMutation({
    mutationFn: (params: { relayId: string; fromEventId?: string; toEventId?: string }) =>
      api.post("buzz/audit-logs/verify", { json: params }).json<BuzzAuditVerifyResult>(),
  })
}
